import { Form, useFormikContext } from 'formik';
import { ArrowUp } from 'lucide-react';

import { HelmRelease } from '@/react/kubernetes/helm/types';
import { RegistryValue } from '@/react/kubernetes/helm/components/HelmRegistrySelect';
import { UseGitFormSection } from '@/react/kubernetes/helm/install/UseGitFormSection';

import { Modal } from '@@/modals';
import { Button, LoadingButton } from '@@/buttons';
import { WidgetTitle } from '@@/Widget';
import { Alert } from '@@/Alert';
import { Checkbox } from '@@/form-components/Checkbox';
import { Tooltip } from '@@/Tip/Tooltip';

import { UpgradeHelmFormValues } from './types';
import { ManualUpgradeFormSections } from './ManualUpgradeFormSections';
import { GitUpgradeFormSections } from './GitUpgradeFormSections';

interface Props {
  release: HelmRelease;
  onDismiss: () => void;
  chartReferenceInfoMessage?: string;
  isCheckingChartReference: boolean;
  initialRegistryValue?: RegistryValue;
  chartReferenceState: {
    requiresSourceConfirmation: boolean;
    requiresChartConfirmation: boolean;
  };
  setPreviewIsValid: (isValid: boolean) => void;
  hasStackError: boolean;
}

export function UpgradeHelmInnerForm({
  release,
  onDismiss,
  chartReferenceInfoMessage,
  isCheckingChartReference,
  initialRegistryValue,
  chartReferenceState,
  setPreviewIsValid,
  hasStackError,
}: Props) {
  const { values, setFieldValue, isSubmitting, submitForm, isValid, dirty } =
    useFormikContext<UpgradeHelmFormValues>();

  return (
    <Form>
      <Modal
        onDismiss={onDismiss}
        size="xl"
        className="flex flex-col h-[80vh] px-0"
        aria-label="upgrade-helm"
      >
        <Modal.Header
          title={
            <WidgetTitle
              className="px-5 block"
              title={`Edit/Upgrade ${release.name}`}
              icon={ArrowUp}
            />
          }
          className="border-b border-solid border-0 border-gray-5 th-dark:border-gray-7 th-highcontrast:border-white"
        >
          <p className="text-sm pt-2 px-5 m-0">
            Namespace &apos;{release.namespace}&apos;
          </p>
        </Modal.Header>
        <div className="flex-1 overflow-y-auto px-5">
          <Modal.Body>
            <div className="form-horizontal">
              {hasStackError && (
                <Alert color="error" className="mb-4">
                  Unable to load Git configuration for this release. Please add
                  the Git configuration again.
                </Alert>
              )}
              <UseGitFormSection
                value={values.useGitOps}
                onChange={(value) => setFieldValue('useGitOps', value)}
              />
              {values.useGitOps ? (
                <GitUpgradeFormSections
                  setPreviewIsValid={setPreviewIsValid}
                  release={release}
                />
              ) : (
                <ManualUpgradeFormSections
                  release={release}
                  chartReferenceInfoMessage={chartReferenceInfoMessage}
                  isCheckingChartReference={isCheckingChartReference}
                  initialRegistryValue={initialRegistryValue}
                  chartReferenceState={chartReferenceState}
                  setPreviewIsValid={setPreviewIsValid}
                />
              )}
            </div>
          </Modal.Body>
        </div>
        <div className="px-5 border-solid border-0 border-t border-gray-5 th-dark:border-gray-7 th-highcontrast:border-white">
          <Modal.Footer>
            <div className="flex flex-1 justify-between">
              <Button
                onClick={onDismiss}
                color="secondary"
                key="cancel-button"
                size="medium"
                data-cy="cancel-button-cy"
              >
                Cancel
              </Button>
              <div className="flex-1 flex gap-4 items-center justify-end">
                {values.useGitOps && (
                  <span className="flex items-center">
                    <Checkbox
                      id="redeploy-now-checkbox"
                      label="Upgrade now"
                      checked={values.redeployNow || false}
                      onChange={(e) =>
                        setFieldValue('redeployNow', e.target.checked)
                      }
                      data-cy="redeploy-now-checkbox"
                    />
                    <Tooltip message="Upgrade the release now, instead of waiting for the next poll/webhook to trigger the upgrade." />
                  </span>
                )}
                <LoadingButton
                  type="submit"
                  color="primary"
                  key="update-button"
                  size="medium"
                  onClick={submitForm}
                  disabled={isSubmitting || !isValid || !dirty}
                  data-cy="update-button-cy"
                  loadingText="Upgrading Helm release..."
                  isLoading={isSubmitting}
                >
                  {values.useGitOps ? 'Edit configuration' : 'Upgrade'}
                </LoadingButton>
              </div>
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </Form>
  );
}
